import React, { useCallback, useState } from 'react';
import Page from '../../components/Page/Page';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styles from './SearchPage.module.less';
import { searchActiveDeliveries, searchPastDeliveries } from '../../api/deliveries';
import { useInfiniteQuery, useQuery } from 'react-query';
import { SearchResult } from './SearchResults';
import { fetchTrackingDeliveriesData } from '../../api/deliveriesTracking';
import { DeliveryStatus } from '../../api/models/deliveries';
import Title from '../../components/Page/Title';
import { fetchDeliveriesTracking } from '../../api/tracking';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';
import { useSelector } from 'react-redux';
import { userSelector } from '../../redux/selectors/user';

const searchForActiveDeliveries = ({ queryKey }: { queryKey: [string, string] }) => {
  const [, term] = queryKey;
  return searchActiveDeliveries(term);
};

const trackDeliveries = ({ queryKey }: { queryKey: [string, string[]] }) => {
  const [, deliveriesIds] = queryKey;

  return fetchTrackingDeliveriesData(deliveriesIds);
};

const trackDeliveriesTransporter = () => {
  return fetchDeliveriesTracking();
};

export const PAST_DELIVERIES_LIMIT = 20;

const searchForPastDeliveries = ({ pageParam = 0, queryKey }: { pageParam?: number; queryKey: [string, string] }) => {
  const [, term] = queryKey;
  return searchPastDeliveries({ start: pageParam * PAST_DELIVERIES_LIMIT, limit: PAST_DELIVERIES_LIMIT, term });
};

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

const allowedStatuses = [DeliveryStatus.Assigned, DeliveryStatus.InProgress];

const removePlusFromPhone = (phone: string | null = '') => {
  if (!phone) {
    return '';
  }
  return phone.replace(/^\+(\d+)/, '$1');
};

const SearchPage = () => {
  const { t } = useTranslation();
  const query = useURLQuery();
  const [searchText, setSearchText] = useState(removePlusFromPhone(query.get('term')));
  const { userGalileoVars } = useSelector(userSelector);
  const isTransporterTrackingEnabled = userGalileoVars[FeatureFlag.IS_TRANSPORTER_TRACKING_ENABLED];

  const fetchTrackingQueryFn = isTransporterTrackingEnabled ? trackDeliveriesTransporter : trackDeliveries;

  const { data: activeDeliveries, isFetching: isFetchingActiveDeliveries } = useQuery(
    ['searchActiveDeliveries', searchText],
    //@ts-ignore
    searchForActiveDeliveries,
    {
      enabled: Boolean(searchText)
    }
  );

  const { data: trackingData } = useQuery(
    [
      'trackDeliveries',
      activeDeliveries?.filter(delivery => allowedStatuses.includes(delivery.status)).map(delivery => delivery.id)
    ],
    //@ts-ignore
    fetchTrackingQueryFn,
    {
      enabled: Boolean(activeDeliveries?.length)
    }
  );
  const {
    data: pastDeliveries,
    isFetching: isFetchingPastDeliveries,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery(
    ['searchPastDeliveries', searchText],
    //@ts-ignore
    searchForPastDeliveries,
    {
      getNextPageParam: (lastGroup, groups) => {
        return lastGroup.length >= PAST_DELIVERIES_LIMIT ? groups.length : undefined;
      },
      enabled: Boolean(searchText)
    }
  );
  const onSearch = useCallback(
    (term: string) => {
      setSearchText(removePlusFromPhone(term));
    },
    [setSearchText, removePlusFromPhone]
  );

  const loadMorePastDeliveries = useCallback(() => fetchNextPage(), [fetchNextPage]);
  const showNotFound =
    Boolean(searchText) &&
    !isFetchingPastDeliveries &&
    !isFetchingActiveDeliveries &&
    !activeDeliveries?.length &&
    !pastDeliveries?.pages.flat().length;
  return (
    <Page>
      <div>
        <Title text={t('search.title')} />
      </div>
      <div className={styles.searchContainer}>
        <Input.Search
          data-testid="searchInput"
          defaultValue={query.get('term') || ''}
          autoFocus
          placeholder={t('search.placeholder')}
          onSearch={onSearch}
          disabled={isFetchingActiveDeliveries || isFetchingPastDeliveries}
        />
      </div>
      <div className={styles.searchResultContainer}>
        <SearchResult
          trackingData={trackingData}
          isActiveDeliveriesLoading={isFetchingActiveDeliveries}
          isPastDeliveriesLoading={isFetchingPastDeliveries}
          activeDeliveries={activeDeliveries || []}
          pastDeliveries={pastDeliveries?.pages.flat() || []}
          loadMorePastDeliveries={loadMorePastDeliveries}
          hasMore={hasNextPage}
          showNotFound={showNotFound}
          searchText={searchText}
        />
      </div>
    </Page>
  );
};

export default SearchPage;
